import { Row, Col, Card, CardBody, CardTitle, Badge } from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { RelativeTime } from '../../components/RelativeTime';
import { useDmarcOverview, useDmarcReports } from './useDmarc';

/**
 * #62 — DMARC-Berichte (Betreiber-Sicht, alle Kunden). Nur lesend. Oben das
 * Aggregat je Domain über die letzten Tage („wie viel Post besteht DMARC?"),
 * darunter die einzelnen aus dem Postmaster-Postfach eingelesenen Berichte.
 */
const fmt = new Intl.NumberFormat('de-DE');
const passRate = (passed, total) => (total ? Math.round((Number(passed) / Number(total)) * 100) : null);

function rateBadge(pct) {
  if (pct == null) return <span className="text-muted">—</span>;
  const color = pct >= 98 ? 'success' : pct >= 90 ? 'warning' : 'danger';
  return <Badge color={color}>{pct} %</Badge>;
}

export function NexusDmarcPage() {
  const overview = useDmarcOverview();
  const reports = useDmarcReports();

  const overviewItems = overview.data?.items ?? [];
  const reportItems = reports.data?.items ?? [];

  return (
    <div>
      <PageHeader title="DMARC-Berichte" subtitle="Aggregat-Berichte aus dem Postmaster-Postfach (#62)" />

      <Row>
        <Col xs={12}>
          <Card className="mb-3">
            <CardBody>
              <CardTitle tag="h6">Übersicht je Domain (letzte 30 Tage)</CardTitle>
              <DataTable
                loading={overview.isLoading}
                error={overview.error}
                empty={!overview.isLoading && overviewItems.length === 0}
                emptyMessage="Keine DMARC-Daten im Zeitraum."
                onRetry={() => overview.refetch()}
              >
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th className="text-end">Berichte</th>
                    <th className="text-end">Nachrichten</th>
                    <th className="text-end">Bestanden</th>
                    <th className="text-end">Gescheitert</th>
                    <th className="text-end">Pass-Rate</th>
                    <th style={{ width: 130 }}>Zuletzt</th>
                  </tr>
                </thead>
                <tbody>
                  {overviewItems.map((it) => (
                    <tr key={it.domain}>
                      <td className="small">{it.domain}</td>
                      <td className="text-end small">{fmt.format(it.berichte ?? 0)}</td>
                      <td className="text-end small">{fmt.format(it.nachrichten ?? 0)}</td>
                      <td className="text-end small text-success">{fmt.format(it.bestanden ?? 0)}</td>
                      <td className="text-end small">
                        {it.gescheitert ? <span className="text-danger">{fmt.format(it.gescheitert)}</span> : fmt.format(0)}
                      </td>
                      <td className="text-end">{rateBadge(passRate(it.bestanden, it.nachrichten))}</td>
                      <td>{it.zuletzt ? <RelativeTime iso={it.zuletzt} /> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <Card className="mb-3">
            <CardBody>
              <CardTitle tag="h6">Einzelberichte</CardTitle>
              <DataTable
                loading={reports.isLoading}
                error={reports.error}
                empty={!reports.isLoading && reportItems.length === 0}
                emptyMessage="Keine Berichte eingelesen."
                onRetry={() => reports.refetch()}
              >
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>Reporter</th>
                    <th>Report-ID</th>
                    <th className="text-end">Nachrichten</th>
                    <th className="text-end">Bestanden</th>
                    <th className="text-end">Gescheitert</th>
                    <th className="text-end">Pass-Rate</th>
                    <th style={{ width: 130 }}>Empfangen</th>
                  </tr>
                </thead>
                <tbody>
                  {reportItems.map((r) => (
                    <tr key={r.id}>
                      <td className="small">{r.domain}</td>
                      <td className="small text-muted">{r.org_name}</td>
                      <td className="font-monospace small">{r.report_id}</td>
                      <td className="text-end small">{fmt.format(r.messages_total ?? 0)}</td>
                      <td className="text-end small text-success">{fmt.format(r.messages_passed ?? 0)}</td>
                      <td className="text-end small">
                        {r.messages_failed ? <span className="text-danger">{fmt.format(r.messages_failed)}</span> : fmt.format(0)}
                      </td>
                      <td className="text-end">{rateBadge(passRate(r.messages_passed, r.messages_total))}</td>
                      <td>{r.received_at ? <RelativeTime iso={r.received_at} /> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
